import { useEffect, useState, type ReactNode, type Ref } from 'react';
import { useGetList } from 'react-admin';
import { Autocomplete, TextField, type AutocompleteRenderInputParams, type SxProps, type Theme } from '@mui/material';

export interface AdminUserOption {
  id: string;
  name?: string | null;
  email?: string | null;
}

export const getAdminUserLabel = (user: AdminUserOption) =>
  [user.name?.trim(), user.email?.trim()].filter(Boolean).join(' · ') || user.id;

interface CommonProps {
  label?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: ReactNode;
  id?: string;
  name?: string;
  inputRef?: Ref<HTMLInputElement>;
  onBlur?: () => void;
  testId?: string;
  sx?: SxProps<Theme>;
}

type Props = CommonProps & (
  | { multiple: true; value: AdminUserOption[]; onChange: (users: AdminUserOption[]) => void }
  | { multiple?: false; value: AdminUserOption | null; onChange: (user: AdminUserOption | null) => void }
);

/** Shared Heart Bank picker: search names/emails on the server, retain selected records. */
export function UserAutocomplete(props: Props) {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 250);
    return () => clearTimeout(timer);
  }, [search]);

  const users = useGetList<AdminUserOption>('users', {
    pagination: { page: 1, perPage: 25 },
    sort: { field: 'createdAt', order: 'DESC' },
    filter: { q: debouncedSearch },
  }, { enabled: !props.disabled });

  const common = {
    fullWidth: true,
    disabled: props.disabled,
    id: props.id,
    sx: props.sx,
    options: users.data ?? [],
    filterOptions: (options: AdminUserOption[]) => options,
    loading: users.isFetching || search !== debouncedSearch,
    noOptionsText: users.isError ? 'Could not load users. Search again to retry.' : 'No users found',
    isOptionEqualToValue: (a: AdminUserOption, b: AdminUserOption) => a.id === b.id,
    getOptionKey: (user: AdminUserOption) => user.id,
    getOptionLabel: getAdminUserLabel,
    onInputChange: (_: unknown, value: string, reason: string) => {
      if (reason === 'input') setSearch(value);
      else if (reason === 'clear' || reason === 'reset' || reason === 'selectOption') setSearch('');
    },
    onBlur: props.onBlur,
  };
  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField {...params}
      label={props.label ?? 'User'} name={props.name} inputRef={props.inputRef}
      required={props.required && (!props.multiple || props.value.length === 0)}
      error={props.error || users.isError}
      helperText={users.isError ? 'Could not load users. Search again to retry.' : props.helperText ?? 'Search by name or email'}
      inputProps={{ ...params.inputProps, 'data-testid': props.testId }}
    />
  );

  return props.multiple ? (
    <Autocomplete {...common} multiple filterSelectedOptions value={props.value}
      onChange={(_, value) => props.onChange(value)} renderInput={renderInput} />
  ) : (
    <Autocomplete {...common} value={props.value}
      onChange={(_, value) => props.onChange(value)} renderInput={renderInput} />
  );
}
